<script setup lang="ts">
import type { Schemas } from "#shopware";
import LayoutCustomPopup from "./LayoutCustomPopup.vue";

import { getCategoryRoute, getTranslatedProperty } from "@shopware/helpers";
import {useCachedPaymentMethods} from "~/components/server/useCachedPaymentMethods";

const { navigationElements } = useNavigation({ type: "footer-navigation" });
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const { getShippingMethods } = useCheckout();
const payments = await useCachedPaymentMethods();
const shipping = await getShippingMethods();

const { apiClient } = useShopwareContext();
const paymentMediaUrls = ref<string[]>([]);
const shippingMediaUrls = ref<string[]>([]);

const fetchMediaDetails = async (mediaIds: string[]): Promise<any[]> => {
  try {
    const response = await apiClient.invoke("readMedia post /media", {
      body: {
        ids: mediaIds,
      },
    });
    return response.data.map((media: any) => media);
  } catch (error) {
    console.error("Failed to fetch media details:", error);
    return [];
  }
};

watch(
  () => shipping.value,
  async () => {
    const mediaIds: string[] = [];
    // Extract media IDs from shipping
    shipping.value.forEach((item) => {
      if (item.customFields?.plubel_custom_image) {
        mediaIds.push(item.customFields.plubel_custom_image);
      }
    });
    if (mediaIds.length > 0) {
      shippingMediaUrls.value = await fetchMediaDetails(mediaIds);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <LayoutCustomPopup />
    <footer class="footer">
      <div class="top-footer bg-cust_cream py-2">
        <div class="container">
          <div class="top-footer-in">
            <ul
              class="flex flex-wrap justify-center md:justify-between text-sm md:text-base"
            >
              <li class="hidden sm:block">
                <span
                  class="f_ico w-8 h-8 inline-flex items-center justify-center"
                >
                  <i class="fa-regular fa-clock"></i
                ></span>
                {{ $t("plubel.footer.fastDelivery") }}
              </li>
              <li class="hidden sm:flex items-center">
                <span
                  class="f_ico w-8 h-8 inline-flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M10.666 13.3333V9.33333V9.33333C10.666 6.388 13.054 4 15.9993 4V4C18.9447 4 21.3327 6.388 21.3327 9.33333V9.33333V13.3333"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M16.0007 18.6667V22.6667"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M22.666 27.9999H9.33268C7.85935 27.9999 6.66602 26.8066 6.66602 25.3333V15.9999C6.66602 14.5266 7.85935 13.3333 9.33268 13.3333H22.666C24.1393 13.3333 25.3327 14.5266 25.3327 15.9999V25.3333C25.3327 26.8066 24.1393 27.9999 22.666 27.9999Z"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span class="flex h-[20px]">{{
                  $t("plubel.footer.securePayment")
                }}</span>
              </li>
              <li class="flex items-center">
                <span
                  class="f_ico w-8 h-8 inline-flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M24.5157 10.6646H25.9309C27.0781 10.6646 28.0065 11.5929 28.0065 12.7401V25.9309C28.0065 27.0767 27.0781 28.0051 25.9309 28.0051H11.4076C10.2604 28.0051 9.33203 27.0767 9.33203 25.9296V24.0034"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M5.33144 24.0035H20.4204C21.3128 24.0035 22.1451 23.558 22.64 22.815L23.6191 21.3451C24.2033 20.4687 24.5154 19.439 24.5154 18.3852V7.99688C24.5154 6.52293 23.3216 5.3291 21.8477 5.3291H8.50876C7.03482 5.3291 5.84099 6.52293 5.84099 7.99688V17.4088C5.84099 18.2371 5.64757 19.0535 5.27809 19.7951L4.13894 22.0734C3.69476 22.9604 4.34036 24.0035 5.33144 24.0035Z"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M18.2031 12.7896L14.4508 16.5418L12.1992 14.2915"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M11.1728 3.99512V6.66289"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M19.1767 3.99512V6.66289"
                      stroke="#35363B"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span class="flex h-[20px]">{{
                  $t("plubel.footer.returnPolicy")
                }}</span>
              </li>
              <li class="hidden lg:block">
                <span
                  class="f_ico w-8 h-8 inline-flex items-center justify-center"
                  ><i class="fa-regular fa-comment"></i
                ></span>
                {{ $t("plubel.footer.consultation") }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div
        class="bg-cust_gray text-base xl:text-lg py-14 md:py-16 lg:py-20 text-white tracking-[0.36px]"
      >
        <div class="container">
          <div class="footer_top flex flex-wrap mb-20 gap-y-12">
            <div
              class="w-full items-center lg:items-start flex gap-16 xl:w-[320px]"
            >
              <NuxtLink :to="formatLink(`/`)" class="text-[0px]">
                <img
                  src="/assets/images/f_logo.svg"
                  alt="logo of the shop"
                  class="w-[140px] lg:w-[158px]"
                />
              </NuxtLink>
              <ul class="flex xl:hidden gap-3 lg:gap-5 text-[28px] lg:text-4xl">
                <li>
                  <a :href="$t('plubel.footer.pinterest')" target="_blank"
                    ><i class="fa-brands fa-pinterest"></i
                  ></a>
                </li>
                <li>
                  <a :href="$t('plubel.footer.instagram')" target="_blank"
                    ><i class="fa-brands fa-instagram"></i
                  ></a>
                </li>
                <li>
                  <a :href="$t('plubel.footer.facebook')" target="_blank"
                    ><i class="fa-brands fa-facebook"></i
                  ></a>
                </li>
              </ul>
            </div>
            <div class="w-full xl:w-[calc(100%-320px)]">
              <div class="flex flex-wrap justify-between w-full gap-y-5">
                <div class="w-full gap-y-12 lg:w-3/4 flex flex-wrap">
                  <div class="w-full md:w-[46%] lg:w-[42%] pe-2">
                    <h6 class="mb-5 xl:mb-7 text-xl font-bold">
                      {{ $t("plubel.footer.contact") }}
                    </h6>
                    <p>
                      {{ $t("plubel.footer.support") }} <br />
                      <a
                        :href="'tel:' + $t('plubel.footer.phone2')"
                        class="font-bold"
                        >{{ $t("plubel.footer.phone") }}</a
                      >
                      <br />
                      {{ $t("plubel.footer.supportDays") }} <br />
                      {{ $t("plubel.footer.viaContact") }}
                    </p>
                  </div>
                  <div
                    class="w-full md:w-[27%] lg:w-[29%] accrod-footer"
                    v-for="navigationElement in navigationElements"
                    :key="navigationElement.id"
                  >
                    <h6 class="mb-5 xl:mb-7 text-xl font-bold active">
                      {{ getTranslatedProperty(navigationElement, "name") }}
                    </h6>
                    <ul class="block">
                      <li
                        v-for="navigationChild in navigationElement.children"
                        :key="navigationChild.id"
                      >
                        <NuxtLink
                          :target="
                            navigationChild.externalLink ||
                            navigationChild.linkNewTab
                              ? '_blank'
                              : ''
                          "
                          :to="formatLink(getCategoryRoute(navigationChild))"
                        >
                          {{ getTranslatedProperty(navigationChild, "name") }}
                        </NuxtLink>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="w-full md:w-1/2 lg:w-[180px] mt-4">
                  <h6 class="mb-5 text-xl font-bold lg:text-center">
                    {{ $t("plubel.footer.safeShopping") }}
                  </h6>
                  <div class="flex flex-wrap gap-4">
                    <a
                      href="https://www.trstd.com/de-ch/reviews/pluebel-ch"
                      target="_blank"
                    >
                        <img
                          class="lg:m-auto"
                          src="/assets/images/trusted_mobile.png"
                          width="76"
                          height="74"
                          alt="trusted shop icon"
                        />
                      </a>
                      <a
                      href="https://www.trstd.com/de-ch/reviews/pluebel-ch"
                      target="_blank"
                    >
                        <img
                          class="lg:m-auto"
                          src="/assets/images/award.png"
                          width="76"
                          height="74"
                          alt="digital commerce award"
                        />
                      </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="w-full md:w-[320px] hidden xl:block">
              <ul class="flex gap-5 text-4xl">
                <li>
                  <a :href="$t('plubel.footer.pinterest')" target="_blank"
                    ><i class="fa-brands fa-pinterest"></i
                  ></a>
                </li>
                <li>
                  <a :href="$t('plubel.footer.instagram')" target="_blank"
                    ><i class="fa-brands fa-instagram"></i
                  ></a>
                </li>
                <li>
                  <a :href="$t('plubel.footer.facebook')" target="_blank"
                    ><i class="fa-brands fa-facebook"></i
                  ></a>
                </li>
              </ul>
            </div>

            <div class="w-full xl:w-[calc(100%-320px)]">
              <div class="flex flex-wrap justify-between w-full gap-y-6">
                <div class="w-full md:w-[46%] md:pe-5">
                  <p>
                    {{ $t("plubel.footer.newsletterText") }}
                  </p>
                </div>
                <FormsNewsLetterForm />
              </div>
            </div>
          </div>

          <div class="footer-partners flex flex-wrap mb-16 lg:mb-20">
            <div class="w-full xl:w-[320px] hidden xl:block"></div>
            <div class="w-full xl:w-[calc(100%-320px)]">
              <div class="w-full flex flex-wrap gap-y-10">
                <div class="md:w-[180px] xxl:w-[240px]">
                  <h6 class="mb-4 text-lg font-light">
                    {{ $t("plubel.footer.shipping") }}
                  </h6>
                  <ul class="flex flex-wrap items-center gap-7">
                    <li v-for="shippingMethod in shippingMediaUrls">
                      <NuxtImg
                        :src="shippingMethod.url"
                        :alt="shippingMethod.alt || shippingMethod.fileName"
                        loading="lazy"
                        class="object-contain"
                        :height="shippingMethod.metaData.height"
                      />
                    </li>
                  </ul>
                </div>
                <div class="md:w-[calc(100%-180px)] xxl:w-[calc(100%-240px)]">
                  <h6 class="mb-4 text-lg">
                    {{ $t("plubel.footer.payments") }}
                  </h6>
                  <ul
                    class="flex flex-wrap items-center gap-4 md:gap-5 xxl:gap-7"
                  >
                    <li v-if="payments" v-for="singlePaymentMethod in payments">
                      <NuxtImg
                        v-if="singlePaymentMethod.media"
                        :src="singlePaymentMethod.media.url"
                        :alt="singlePaymentMethod.translated.name"
                        loading="lazy"
                        format="webp"
                        class="object-contain"
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="copy-sec flex-wrap flex text-[14px] md:text-base gap-y-4">
            <div class="copy-left w-full lg:w-[320px]">
              <p>© {{ $t("plubel.footer.copyRights") }}</p>
            </div>
            <div
              class="copy-right w-full lg:w-[calc(100%-320px)] -order-1 lg:order-1"
            >
              <p>* {{ $t("plubel.footer.taxincludedMsg") }}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>
